import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_4e8f42ea from 'nuxt_plugin_plugin_4e8f42ea' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_sentryserver_7d745e36 from 'nuxt_plugin_sentryserver_7d745e36' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_5b8c5f5d from 'nuxt_plugin_sentryclient_5b8c5f5d' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_pluginutils_316d123a from 'nuxt_plugin_pluginutils_316d123a' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_4071f905 from 'nuxt_plugin_pluginrouting_4071f905' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_31b8188a from 'nuxt_plugin_pluginmain_31b8188a' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_httpserver_4b3d1a4e from 'nuxt_plugin_httpserver_4b3d1a4e' // Source: ./http.server.js (mode: 'server')
import nuxt_plugin_http_7ff9080f from 'nuxt_plugin_http_7ff9080f' // Source: ./http.js (mode: 'all')
import nuxt_plugin_apollomodule_29d24626 from 'nuxt_plugin_apollomodule_29d24626' // Source: ./apollo-module.js (mode: 'all')
import nuxt_plugin_nuxtmq_14a0bee9 from 'nuxt_plugin_nuxtmq_14a0bee9' // Source: ./nuxt-mq.js (mode: 'all')
import nuxt_plugin_customGtm_650b0e1e from 'nuxt_plugin_customGtm_650b0e1e' // Source: ../../../plugins/customGtm.js (mode: 'client')
import nuxt_plugin_init_6a81d586 from 'nuxt_plugin_init_6a81d586' // Source: ../../../plugins/init (mode: 'client')
import nuxt_plugin_preview_f040bdb6 from 'nuxt_plugin_preview_f040bdb6' // Source: ../../../plugins/preview.js (mode: 'client')
import nuxt_plugin_rect_2757995d from 'nuxt_plugin_rect_2757995d' // Source: ../../../plugins/rect.js (mode: 'client')
import nuxt_plugin_scroll_ee90a558 from 'nuxt_plugin_scroll_ee90a558' // Source: ../../../plugins/scroll.js (mode: 'client')
import nuxt_plugin_vscroll_297f750c from 'nuxt_plugin_vscroll_297f750c' // Source: ../../../plugins/v-scroll (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: function() {
    const i18nHead = this.$nuxtI18nHead ?
    this.$nuxtI18nHead({ addSeoAttributes: true }) :
    { htmlAttrs: [], meta: [], link: [] };
    const themeColor = process.env.themeColor;
    const meta = process.env.meta;

    return {
      htmlAttrs: {
        ...i18nHead.htmlAttrs
      },
      title: 'Safewill',
      meta: [
      {
        charset: 'utf-8'
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1'
      },
      {
        name: 'msapplication-TileColor',
        content: '#ccf6e8'
      },
      {
        name: 'application-name',
        content: 'Safewill'
      },
      {
        name: 'msapplication-config',
        content: '/favicon/browserconfig.xml'
      },
      {
        name: 'theme-color',
        content: themeColor
      },
      {
        hid: 'description',
        name: 'description',
        content: meta.description
      },
      {
        hid: 'og:title',
        property: 'og:title',
        content: meta.title
      },
      {
        property: 'og:site_name',
        content: meta.title
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: meta.description
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: meta.url
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: meta.opengraph
      },
      {
        property: 'og:image:width',
        content: '1200'
      },
      {
        property: 'og:image:height',
        content: '630'
      },
      {
        name: 'twitter:card',
        content: 'summary_image_large'
      },
      {
        hid: 'twitter:url',
        name: 'twitter:url',
        content: meta.url
      },
      {
        hid: 'twitter:title',
        name: 'twitter:title',
        content: meta.title
      },
      {
        hid: 'twitter:description',
        name: 'twitter:description',
        content: meta.description
      },
      {
        hid: 'twitter:image',
        name: 'twitter:image',
        content: meta.opengraph
      },
      ...i18nHead.meta],

      link: [
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/favicon/apple-touch-icon.png'
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon/favicon-32x32.png'
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon/favicon-16x16.png'
      },
      {
        rel: 'manifest',
        href: '/favicon/site.webmanifest'
      },
      {
        rel: 'mask-icon',
        href: '/favicon/safari-pinned-tab.svg',
        color: themeColor
      },
      {
        rel: 'stylesheet',
        href: 'https://hello.myfonts.net/count/3a6c74',
        media: 'none'
      },
      {
        rel: 'preconnect',
        href: 'https://storage.googleapis.com/cms-safewill-operation-australia-southeast1'
      },
      {
        rel: 'preconnect',
        href: 'https://analytics.google.com'
      },
      {
        rel: 'preconnect',
        href: 'https://c.bing.com'
      },
      {
        rel: 'preconnect',
        href: 'https://c.clarity.ms'
      },
      {
        rel: 'preconnect',
        href: 'https://googleads.g.doubleclick.net'
      },
      {
        rel: 'preconnect',
        href: 'https://i.clarity.ms'
      },
      {
        rel: 'preconnect',
        href: 'https://in.hotjar.com'
      },
      {
        rel: 'preconnect',
        href: 'https://j.clarity.ms'
      },
      {
        rel: 'preconnect',
        href: 'https://vars.hotjar.com'
      },
      {
        rel: 'preconnect',
        href: 'https://www.facebook.com'
      },
      {
        rel: 'preconnect',
        href: 'https://www.google.com'
      },
      {
        rel: 'preconnect',
        href: 'https://www.google.com.au'
      },
      {
        rel: 'preconnect',
        href: 'https://calendly.com'
      },
      ...i18nHead.link]
    };
  },

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_4e8f42ea === 'function') {
    await nuxt_plugin_plugin_4e8f42ea(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_7d745e36 === 'function') {
    await nuxt_plugin_sentryserver_7d745e36(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_5b8c5f5d === 'function') {
    await nuxt_plugin_sentryclient_5b8c5f5d(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_316d123a === 'function') {
    await nuxt_plugin_pluginutils_316d123a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_4071f905 === 'function') {
    await nuxt_plugin_pluginrouting_4071f905(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_31b8188a === 'function') {
    await nuxt_plugin_pluginmain_31b8188a(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_httpserver_4b3d1a4e === 'function') {
    await nuxt_plugin_httpserver_4b3d1a4e(app.context, inject)
  }

  if (typeof nuxt_plugin_http_7ff9080f === 'function') {
    await nuxt_plugin_http_7ff9080f(app.context, inject)
  }

  if (typeof nuxt_plugin_apollomodule_29d24626 === 'function') {
    await nuxt_plugin_apollomodule_29d24626(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtmq_14a0bee9 === 'function') {
    await nuxt_plugin_nuxtmq_14a0bee9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_customGtm_650b0e1e === 'function') {
    await nuxt_plugin_customGtm_650b0e1e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_init_6a81d586 === 'function') {
    await nuxt_plugin_init_6a81d586(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_preview_f040bdb6 === 'function') {
    await nuxt_plugin_preview_f040bdb6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_rect_2757995d === 'function') {
    await nuxt_plugin_rect_2757995d(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_scroll_ee90a558 === 'function') {
    await nuxt_plugin_scroll_ee90a558(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vscroll_297f750c === 'function') {
    await nuxt_plugin_vscroll_297f750c(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
